import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BRAND, PUBLIC_ORIGIN, HOCKEY, FOOTBALL } from './shareConfig';
import './ShareButton.css';

export function sharePath(path) {
  let page;
  try { page = new URL(path, 'https://hq.invalid'); } catch { return null; }
  if (page.origin !== 'https://hq.invalid') return null;
  const source = page.searchParams;
  if (FOOTBALL) {
    const params = new URLSearchParams();
    const copy = key => { if (source.get(key)) params.set(key, source.get(key)); };
    if (['/match','/lineup'].includes(page.pathname)) {
      const identity = (source.get('url') || '').match(/^hq:\/\/football\/([^/]+)\/([1-9]\d*)(?:\/laguppstallning)?$/);
      if (identity) { params.set('simulation',identity[1]); params.set('id',identity[2]); }
      else { copy('simulation'); copy('id'); }
      if (!params.get('simulation') || !/^[1-9]\d*$/.test(params.get('id') || '')) return null;
      return `/match?${params}`;
    }
    if (page.pathname.startsWith('/team/')) { try { decodeURIComponent(page.pathname.slice(6)); } catch { return null; } copy('serie'); }
    else if (['/series','/standings'].includes(page.pathname)) { copy('serie'); copy('vy'); copy('tab'); }
    else if (page.pathname === '/tips') copy('serie');
    else if (page.pathname === '/') copy('date');
    else if (page.pathname !== '/upcoming') return null;
    return `${page.pathname}${params.toString() ? `?${params}` : ''}`;
  }
  const params = new URLSearchParams();
  let kind;
  const copy = key => { if (source.get(key)) params.set(key, source.get(key)); };
  if (['/match', '/lineup'].includes(page.pathname)) {
    kind = 'match';
    if (HOCKEY) { copy('simulation'); copy('id'); }
    else {
      const match = (source.get('url') || '').match(/^https:\/\/stats\.innebandy\.se\/sasong\/(\d+)\/serie\/(\d+)\/match\/(\d+)(?:\/|$)/);
      if (!match) return null;
      ['season', 'competition', 'id'].forEach((key, index) => params.set(key, match[index + 1]));
    }
    copy('tab');
    if (HOCKEY && params.has('tab')) params.set('tab', ({standings:'table',lineup:'teams'})[params.get('tab')] || params.get('tab'));
  } else if (page.pathname.startsWith('/team/')) {
    kind = 'team';
    try { params.set('team', decodeURIComponent(page.pathname.slice(6))); } catch { return null; }
    copy('serie');
    if (!HOCKEY) copy('competition');
  } else if (['/series', '/standings'].includes(page.pathname)) {
    kind = 'series'; copy('serie'); copy(HOCKEY ? 'tab' : 'vy');
    if (HOCKEY && source.get('vy')) params.set('tab',source.get('vy') === 'spelschema' ? 'schedule' : 'table');
  } else if (page.pathname === '/tips') { kind = 'tips'; copy('serie'); }
  else if (page.pathname === '/upcoming') kind = 'schedule';
  else if (page.pathname === '/') { kind = 'day'; copy('date'); }
  else return null;
  return `/share/${kind}${params.toString() ? `?${params}` : ''}`;
}

const ShareIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 16V3m-5 5 5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/></svg>;

export default function ShareButton({ path, title, label = 'Dela', disabled = false }) {
  if (HOCKEY && path?.startsWith("/match?")) {
    const params = new URLSearchParams(path.split("?")[1]);
    const identity = params.get("url")?.match(/^hq:\/\/hockey\/([^/]+)\/([^/]+)/);
    if (identity) { params.set("simulation", identity[1]); params.set("id", identity[2]); path = "/match?" + params; }
  }
  const linkPath = sharePath(path);
  const url = linkPath ? PUBLIC_ORIGIN + linkPath : '';
  const previewUrl = linkPath && !FOOTBALL ? PUBLIC_ORIGIN + linkPath.replace(/(\/share\/[^?]+)(.*)/, '$1.png$2') : '';
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const trigger = useRef(null), dialog = useRef(null), input = useRef(null);
  const close = () => { setOpen(false); setNotice(''); };
  useEffect(() => { close(); }, [url]);
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = dialog.current.querySelector('button');
    first?.focus();
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const elements = [...dialog.current.querySelectorAll('button:not(:disabled), a[href], input')];
      const firstElement = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); firstElement.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown); trigger.current?.focus(); };
  }, [open]);
  const share = async () => {
    setNotice('');
    if (navigator.share) {
      setBusy(true);
      try { await navigator.share({ title: `${title} | ${BRAND}`, text: title, url }); return; }
      catch (error) { if (error.name === 'AbortError') return; }
      finally { setBusy(false); }
    }
    setOpen(true);
  };
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw Error('clipboard');
      await navigator.clipboard.writeText(url);
      setNotice('Länken är kopierad. Klistra in den i din chatt.');
    } catch {
      input.current.focus(); input.current.select();
      setNotice('Länken är markerad. Välj Kopiera och klistra sedan in den i din chatt.');
    }
  };
  return <>
    <button ref={trigger} className="hq-share-button" type="button" onClick={share} disabled={disabled || !url || busy} aria-label={`${label}: ${title}`}><ShareIcon/><span>{label}</span></button>
    {open && createPortal(<div className="hq-share-backdrop" onClick={event => { if (event.target === event.currentTarget) close(); }}>
      <section ref={dialog} className="hq-share-dialog" role="dialog" aria-modal="true" aria-labelledby="hq-share-title" aria-describedby="hq-share-help">
        <div className="hq-share-dialog__heading"><div><span className="hq-share-eyebrow">Skicka vidare</span><h2 id="hq-share-title">Dela med någon</h2></div><button className="hq-share-close" type="button" aria-label="Stäng delning" onClick={close}>×</button></div>
        <div className="hq-share-preview">{previewUrl && <img src={previewUrl} alt="" width="1200" height="630" onError={event => { event.currentTarget.hidden = true; }}/>}<div><small>{BRAND}</small><strong>{title}</strong><span>{new URL(PUBLIC_ORIGIN).hostname}</span></div></div>
        <p id="hq-share-help">Skicka länken i din chatt. Den öppnar samma match, lag eller serie för den som får den.</p>
        <div className="hq-share-actions"><button className="hq-share-copy" type="button" onClick={copy}>{notice.startsWith('Länken är kopierad') ? '✓ Länk kopierad' : 'Kopiera länk'}</button><a href={`https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`} target="_blank" rel="noopener noreferrer">WhatsApp <span aria-hidden="true">↗</span></a></div>
        <label className="hq-share-link">Länk att dela<input ref={input} value={url} readOnly onFocus={event => event.target.select()} /></label>
        <p className="hq-share-notice" role="status" aria-live="polite">{notice || 'På mobilen kan du också välja Signal, Snapchat och andra appar i telefonens delningsmeny.'}</p>
      </section>
    </div>, document.body)}
  </>;
}
